import { Cloud, ExternalLink, MapPin, Users2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, StatusBadge } from "components/dashboard/ui";

const safeNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const clamp = (value, min = 0, max = 100) =>
  Math.min(max, Math.max(min, safeNumber(value)));

const formatCurrencyCompact = (value) => {
  const number = safeNumber(value);
  const abs = Math.abs(number);
  const sign = number < 0 ? "-" : "";

  if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toFixed(1)}Cr`;
  if (abs >= 100000) return `${sign}₹${(abs / 100000).toFixed(1)}L`;
  return `${sign}₹${abs.toLocaleString("en-IN")}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const daysBetween = (start, end) => {
  if (!start || !end) return null;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()))
    return null;
  return Math.max(0, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)));
};

const getStatusTone = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (["active", "completed", "complete"].includes(normalized)) return "good";
  if (["delayed", "overdue", "halted", "cancelled"].includes(normalized))
    return "alert";
  if (["pending", "on hold"].includes(normalized)) return "warn";
  return "neutral";
};

const getInitials = (name) =>
  String(name || "Project")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "PR";

export default function ProjectSummaryHeader({
  summary,
  weather,
  weatherLoading = false,
  weatherError = false,
  source = "Owner",
}) {
  const navigate = useNavigate();
  const { siteId } = useParams();
  const siteOverview = summary?.site_overview || {};
  const tasksOverview = summary?.tasks_overview || {};
  const financeOverview = summary?.finance_overview || {};
  const workforceOverview = summary?.workforce_overview || {};
  const approvalsOverview = summary?.approvals_overview || {};

  const progress = clamp(tasksOverview.progress);
  const estimatedBudget = safeNumber(financeOverview.estimated_budget);
  const totalExpenses = safeNumber(financeOverview.total_expenses);
  const budgetUsed = estimatedBudget
    ? clamp((totalExpenses / estimatedBudget) * 100)
    : 0;
  const pendingApprovals =
    safeNumber(approvalsOverview.pending_subtask_approvals) +
    safeNumber(approvalsOverview.pending_snags) +
    safeNumber(approvalsOverview.pending_material_requests);
  const totalProjectDays = daysBetween(
    siteOverview.start_date,
    siteOverview.handover_date,
  );
  const elapsedProjectDays = siteOverview.start_date
    ? daysBetween(siteOverview.start_date, new Date())
    : null;
  const location = [
    siteOverview.site_address,
    siteOverview.city,
    siteOverview.state,
  ]
    .filter(Boolean)
    .join(", ");
  const statusTone = getStatusTone(siteOverview.status);
  const statusDotClass =
    statusTone === "alert"
      ? "bg-red-600"
      : statusTone === "warn"
        ? "bg-amber-600"
        : statusTone === "good"
          ? "bg-emerald-600"
          : "bg-muted-foreground";
  const weatherDescription = weather?.weather?.[0]?.description;
  const weatherText = weatherLoading
    ? "Weather loading"
    : weatherError || !weather
      ? "Weather unavailable"
      : `${Math.round(safeNumber(weather?.main?.temp))}° ${weatherDescription || "Weather"} · ${safeNumber(weather?.main?.humidity)}%`;

  const summaryStats = [
    {
      label: "Progress",
      value: `${Math.round(progress)}%`,
      badge: siteOverview.status || "Status pending",
      badgeTone: getStatusTone(siteOverview.status),
      bar: progress,
      barClass: "bg-sm-primary",
      sub: `${safeNumber(tasksOverview.completed_tasks)} completed of ${safeNumber(tasksOverview.total_tasks)} tasks`,
    },
    {
      label: "Budget Used",
      value: `${Math.round(budgetUsed)}%`,
      badge: budgetUsed > 100 ? "over budget" : "tracked",
      badgeTone: budgetUsed > 100 ? "alert" : "good",
      bar: budgetUsed,
      barClass: budgetUsed > 100 ? "bg-red-500" : "bg-emerald-500",
      sub: `${formatCurrencyCompact(totalExpenses)} spent of ${estimatedBudget ? formatCurrencyCompact(estimatedBudget) : "-"}`,
    },
    {
      label: "On Site Today",
      value: safeNumber(workforceOverview.workers_present),
      trend: `${safeNumber(workforceOverview.total_workers)} total`,
      trendClass: "text-emerald-600",
      workers: [
        {
          role: "Worker",
          count: safeNumber(workforceOverview.workers_present),
          total: safeNumber(workforceOverview.total_workers),
        },
        {
          role: "Contractor",
          count: safeNumber(workforceOverview.contractor_attendance),
        },
        { role: "WOs", count: safeNumber(workforceOverview.active_workorders) },
      ],
    },
    {
      label: "Needs You",
      value: pendingApprovals,
      valueClass: pendingApprovals ? "text-red-600" : "text-emerald-600",
      badge: `${safeNumber(approvalsOverview.pending_snags)} snags`,
      badgeTone: pendingApprovals ? "alert" : "good",
      sub: `${safeNumber(approvalsOverview.pending_subtask_approvals)} approvals · ${safeNumber(approvalsOverview.pending_material_requests)} MRs`,
    },
  ];

  return (
    <>
      <Card className="col-span-12">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <div className="flex size-16 shrink-0 items-center justify-center rounded-2xl border border-sm-primary/20 bg-sm-primary/10 text-xl font-bold text-sm-primary">
              {getInitials(siteOverview.site_name)}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-2xl font-bold tracking-tight text-foreground">
                  {siteOverview.site_name || "Project"}
                </h2>
                <StatusBadge tone={statusTone} className="px-3 py-1">
                  <span className={`size-1.5 rounded-full ${statusDotClass}`} />
                  {siteOverview.status || "Status pending"}
                </StatusBadge>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm font-medium text-muted-foreground">
                <MapPin className="size-4" />
                <span>{location || "Location unavailable"}</span>
              </div>
            </div>
          </div>
          <div className="flex w-full shrink-0 justify-start lg:w-auto lg:justify-end">
            <button
              type="button"
              onClick={() => navigate(`/site/${siteId}/details`)}
              className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-semibold hover:underline text-foreground transition-colors hover:bg-muted"
            >
              <span>Project Details</span>
              <ExternalLink className="size-4" />
            </button>
          </div>
        </div>

        <div className="mt-3 grid border-t border-border pt-3 text-sm font-semibold text-muted-foreground sm:grid-cols-2 xl:grid-cols-4">
          <div>
            Day{" "}
            <span className="text-foreground">{elapsedProjectDays ?? "-"}</span>{" "}
            of {totalProjectDays ?? "-"}
          </div>
          <div>
            Handover{" "}
            <span className="text-foreground">
              {formatDate(siteOverview.handover_date)}
            </span>
          </div>
          {/* <div>PM <span className="text-foreground">Anjali Sen</span></div> */}
          <div className="inline-flex items-center gap-2">
            <Cloud className="size-4" />
            <span>
              <span className="text-foreground">
                {weatherText.split(" ")[0]}
              </span>{" "}
              {weatherText.split(" ").slice(1).join(" ")}
            </span>
          </div>
        </div>
      </Card>

      {source === "Owner" && (
        <div className="col-span-12 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {summaryStats.map((stat) => (
            <Card key={stat.label}>
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                {stat.label}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <div
                  className={`text-3xl font-bold tracking-tight ${stat.valueClass ?? ""}`}
                >
                  {stat.value}
                </div>
                {stat.badge && (
                  <StatusBadge tone={stat.badgeTone}>{stat.badge}</StatusBadge>
                )}
                {stat.trend && (
                  <span className={`text-sm font-bold ${stat.trendClass}`}>
                    {stat.trend}
                  </span>
                )}
              </div>
              {stat.bar !== undefined && (
                <div className="mt-6 h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full rounded-full ${stat.barClass}`}
                    style={{ width: `${stat.bar}%` }}
                  />
                </div>
              )}
              {stat.workers ? (
                <div className="mt-2 grid grid-cols-3 gap-3 text-sm">
                  {stat.workers.map((worker) => (
                    <div key={worker.role}>
                      <div className="inline-flex items-center gap-1 text-muted-foreground">
                        {worker.role === "Worker" && (
                          <Users2 className="size-3.5" />
                        )}
                        {worker.role}
                      </div>
                      <div className="mt-1 text-lg font-semibold text-foreground">
                        {worker.count}
                        {worker.total && `/${worker.total}`}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-2 text-sm font-medium leading-6 text-muted-foreground">
                  {stat.sub}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
